import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { Button, CircularProgress } from '@material-ui/core';
import BarcodeScannerComponent from "react-qr-barcode-scanner";

const BarcodeScanner = ({ onCodeDetected, onClose, isLoading = false }) => {
    const [codigoBarras, setCodigoBarras] = useState('No result');
    const [lastDetectedCode, setLastDetectedCode] = useState('');
    const [scannerReady, setScannerReady] = useState(true);

    // Referencias para evitar múltiples detecciones
    const scannerTimeout = useRef(null);
    const searchInProgress = useRef(false);

    // Limpiar al desmontar el componente
    useEffect(() => {
        return () => {
            if (scannerTimeout.current) {
                clearTimeout(scannerTimeout.current);
            }

            // Asegúrate de realizar la limpieza necesaria para el escáner
            setScannerReady(false);
        };
    }, []);

    // Manejar la detección de códigos de barras de manera eficiente
    const handleBarcodeDetection = useCallback((err, result) => {
        // Si hay un error o no hay resultado, no hacer nada
        if (err || !result) {
            return;
        }

        const detectedCode = result.text;

        // Validar que el código sea un EAN válido (normalmente 8, 12 o 13 dígitos)
        const isValidEAN = /^[0-9]{8,13}$/.test(detectedCode);

        if (!isValidEAN) {
            return; // Ignorar códigos no válidos
        }

        // Evitar escanear el mismo código repetidamente
        if (detectedCode === lastDetectedCode || searchInProgress.current) {
            return;
        }

        // Actualizar el código detectado
        setLastDetectedCode(detectedCode);
        setCodigoBarras(detectedCode);

        // Beep para confirmar el escaneo (opcional)
        try {
            new Audio('data:audio/mp3;base64,SUQzAwAAAAABOlRTU0UAAAAwAAAATEFNRSA2NGJpdHMgdmVyc2lvbiAzLjEwMABURU5DAAAABQAAAFVuaXF1ZQBUWVhYAAAAEQAAAHdzaXBub2lzZSBmcmVlIHNmZgBUREFUAAAABAAAADIwMjMAVElUMgAAAAEAAABCAFRDT04AAAABAAAAQQA=').play();
        } catch (e) {
            // Silenciar errores de audio (algunos navegadores pueden bloquear la reproducción)
        }

        // Debounce para evitar múltiples llamadas
        if (scannerTimeout.current) {
            clearTimeout(scannerTimeout.current);
        }

        scannerTimeout.current = setTimeout(() => {
            searchInProgress.current = true;
            onCodeDetected(detectedCode);
        }, 800); // Esperar 800ms para asegurar que es un escaneo estable

    }, [lastDetectedCode, onCodeDetected]);

    // Restablecer el escáner
    const resetScanner = () => {
        setLastDetectedCode('');
        setCodigoBarras('No result');
        searchInProgress.current = false;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
            <div className="bg-white p-4 rounded-lg max-w-lg w-full">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-xl font-bold">Escáner de Códigos de Barras</h1>
                    <Button
                        onClick={() => {
                            resetScanner();
                            onClose();
                        }}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X size={24} />
                    </Button>
                </div>

                <div className="relative">
                    {scannerReady && (
                        <BarcodeScannerComponent
                            width={450}
                            height={350}
                            onUpdate={handleBarcodeDetection}
                            facingMode="environment"
                            torch={true}
                            scanDelay={300}
                            constraints={{
                                video: {
                                    facingMode: "environment",
                                    width: { ideal: 1280 },
                                    height: { ideal: 720 },
                                    aspectRatio: { ideal: 1.777778 },
                                    frameRate: { ideal: 25, max: 30 }
                                }
                            }}
                        />
                    )}

                    {/* Guía para alinear el código de barras */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="border-2 border-red-500 w-4/5 h-1/4 rounded opacity-75"></div>
                    </div>

                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                            <CircularProgress color="primary" />
                        </div>
                    )}
                </div>

                <div className="mt-4 text-center">
                    {codigoBarras !== 'No result' ? (
                        <p className="font-mono bg-gray-100 p-2 rounded">
                            Código detectado: {codigoBarras}
                        </p>
                    ) : (
                        <p className="text-gray-600">
                            Alinea el código de barras dentro del recuadro
                        </p>
                    )}
                </div>

                <div className="mt-4 flex justify-center">
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={resetScanner}
                    >
                        Reiniciar escáner
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BarcodeScanner;